// App.tsx

import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Outlet
} from 'react-router-dom';
import Navbar from './components/innoscale/Navbar';
import SiteMap from './components/innoscale/SiteMap';
import LoadingOnePager from './components/innoscale/LoadingOnePager';
import {
  TechScoutingContext,
  StakeholderIdentificationContext,
  ProjectContext,
  MarketResearchContext
} from './ContextStore';
import { StakeholderData, CompanyData, Project, ProjectData } from './types';
import { createPusherInstance } from './utils/pusherConfig';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pusher, { Channel } from 'pusher-js';
import axiosRequest from './utils/axiosRequest';
import './App.css';

// Lazy load the app modules for better performance
const InnoscaleApp = lazy(() => import('./components/innoscale/InnoscaleApp'));
const OutpostPortal = lazy(() => import('./components/outpost/OutpostPortal'));
const EmployeePortal = lazy(() => import('./components/employee/EmployeePortal'));

/**
 * This component protects routes that require authentication.
 * If the user is not authenticated, it redirects them to the /signin route.
 */
interface RequireAuthProps {
  isAuthenticated: boolean;
  children?: JSX.Element;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ isAuthenticated, children }) => {
  const location = useLocation();
  if (!isAuthenticated) {
    // Determine which app the user is trying to access
    const isInnoscaleApp = location.pathname.startsWith('/innoscale');
    const signinPath = isInnoscaleApp ? '/innoscale/signin' : '/outpost/signin';
    
    // Only store the redirect path for the calendar auth route
    if (location.pathname === '/calendar/auth' || location.pathname === '/innoscale/calendar/auth') {
      localStorage.setItem('redirectAfterAuth', location.pathname);
    } else {
      // Make sure we clear any existing redirect path for other routes
      localStorage.removeItem('redirectAfterAuth');
    }
    // Redirect to appropriate sign-in page
    return <Navigate to={signinPath} replace />;
  }
  return children ? children : <Outlet />;
};

const App: React.FC = () => {
  // Global authentication and user state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isInProposalsGroup, setIsInProposalsGroup] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userId, setUserId] = useState<string>('');

  // TechScouting context state
  const [techResponse, setTechResponse] = useState<any>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyData | null>(null);
  const [stakeholderResponse, setStakeholderResponse] = useState<StakeholderData[]>([]);
  const [selectedCompanyOrigin, setSelectedCompanyOrigin] = useState<string | null>(null);
  const [skipFirstPage, setSkipFirstPage] = useState<number | undefined>(undefined);
  const [selectedStakeholder, setSelectedStakeholder] = useState<StakeholderData | null>(null);
  const [selectedStakeholderOrigin, setSelectedStakeholderOrigin] = useState<string | null>(null);

  // MarketResearch context state
  const [marketResearchCompanies, setMarketResearchCompanies] = useState<any[]>([]);
  const [marketResearchPrompt, setMarketResearchPrompt] = useState<string>('');
  const [quadrantCuts, setQuadrantCuts] = useState<{x_cut: number, y_cut: number}>({x_cut: 0.5, y_cut: 0.5});
  const [axisRanges, setAxisRanges] = useState<{x_min: number, x_max: number, y_min: number, y_max: number}>({
    x_min: 0, x_max: 1, y_min: 0, y_max: 1
  });

  // Pusher refs
  const pusherRef = useRef<Pusher | null>(null);
  const channelRef = useRef<Channel | null>(null);

  // Project state
  const [defaultProjectName, setDefaultProjectName] = useState<string>('');
  const [defaultProjectId, setDefaultProjectId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedLibraryProjectId, setSelectedLibraryProjectId] = useState<string | null>(null);

  // Dark mode state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(
    () => localStorage.getItem('darkMode') !== 'false'
  );

  const authURL = process.env.REACT_APP_SIGNIN_API_URL;
  if (!authURL) {
    throw new Error('REACT_APP_SIGNIN_API_URL is not defined');
  }
  const projectsURL = process.env.REACT_APP_PROJECTS_API_URL;
  if (!projectsURL) {
    throw new Error('REACT_APP_PROJECTS_API_URL is not defined');
  }

  const [projects, setProjects] = useState<Project[]>([]);

  // For navigation, use the useNavigate hook.
  const navigate = useNavigate();
  const location = useLocation();

  // Define routes for which the Navbar and SiteMap should be hidden.
  // Only show UI for InnoScale routes, not Portal routes
  const hideUIRoutes = [
    '/signin',
    '/signup',
    '/innoscale/signin',
    '/innoscale/signup',
    '/outpost/signin',
    '/outpost/signup',
    '/outpost/register'
  ];
  
  // Show Navbar and SiteMap only for InnoScale routes (not Portal routes)
  const isInnoscaleRoute = location.pathname.startsWith('/innoscale');
  const showUI = !hideUIRoutes.includes(location.pathname) && isInnoscaleRoute;

  useEffect(() => {
    const theme = isDarkMode ? 'dark-mode' : 'light-mode';
    document.body.classList.add(theme);
    return () => {
      document.body.classList.remove('dark-mode', 'light-mode');
    };
  }, [isDarkMode]);

  // Add portal-mode class for portal/employee routes (pure black background)
  useEffect(() => {
    const isPortalRoute = location.pathname.startsWith('/outpost') || location.pathname.startsWith('/employee');
    if (isPortalRoute) {
      document.body.classList.add('portal-mode');
    } else {
      document.body.classList.remove('portal-mode');
    }
    return () => {
      document.body.classList.remove('portal-mode');
    };
  }, [location.pathname]);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', newMode ? 'true' : 'false');
    document.body.classList.toggle('dark-mode', newMode);
    document.body.classList.toggle('light-mode', !newMode);
  };

  // Pusher initialization
  useEffect(() => {
    const isPortalRoute = location.pathname.startsWith('/outpost') || location.pathname.startsWith('/employee');
    if (userId && isAuthenticated && !isPortalRoute) {
      initializePusher();
    }
    return () => {
      cleanupPusher();
    };
  }, [userId, isAuthenticated, location.pathname]);

  const initializePusher = () => {
    if (pusherRef.current) {
      console.log('Pusher already initialized.');
      return;
    }
    if (!userId) {
      console.error('User ID not found for Pusher.');
      return;
    }
    const pusher = createPusherInstance();
    pusherRef.current = pusher;
    console.log(`Subscribing to private channel for user ${userId}`);
    const channel = pusher.subscribe(`private-user-${userId}`);
    channelRef.current = channel;
    channel.bind('document-event', handleDocumentEvent);
    channel.bind('pusher:subscription_error', handleSubscriptionError);
    console.log('Pusher subscription initialized.');
  };

  const handleDocumentEvent = (data: any) => {
    console.log('Received document event:', data);
    if (data.message === 'document_complete') {
      const { document_url } = data;
      toast.success('Generated Document added to Library!', {
        position: 'top-right',
        autoClose: 5000,
        theme: 'dark',
        icon: (
          <img src="/LogoThumbnailWhite.png" alt="Logo Thumbnail" className="toast-custom-icon" />
        ),
        onClick: () => window.open(document_url, '_blank'),
        className: 'custom-toast-clickable',
      });
    }
  };

  const handleSubscriptionError = (status: number) => {
    console.error('Pusher subscription error:', status);
  };

  const cleanupPusher = () => {
    if (channelRef.current) {
      console.log('Cleaning up Pusher channel subscription');
      channelRef.current.unbind_all();
      channelRef.current.unsubscribe();
      channelRef.current = null;
    }
    if (pusherRef.current) {
      console.log('Disconnecting Pusher instance');
      pusherRef.current.disconnect();
      pusherRef.current = null;
    }
  };

  // Sign in handler: sets authentication and initial project state.
  const handleSignIn = (email: string, uid: string, groups: string[], userProjects: Project[]) => {
    setIsAuthenticated(true);
    setUserEmail(email);
    setUserId(uid);
    setIsInProposalsGroup(Array.isArray(groups) && groups.includes('proposals'));
    setProjects(userProjects);
    
    // Determine which app the user is signing into based on current path
    const isInnoscaleApp = location.pathname.startsWith('/innoscale');
    const isOutpostApp = location.pathname.startsWith('/outpost');
    
    // Check if the user was trying to access calendar/auth
    const redirectPath = localStorage.getItem('redirectAfterAuth');
    const isCalendarAuthAttempt = redirectPath === '/calendar/auth' || redirectPath === '/innoscale/calendar/auth';
    
    // Clear the stored redirect path
    if (redirectPath) {
      localStorage.removeItem('redirectAfterAuth');
    }
    
    if (userProjects && userProjects.length > 0) {
      const defaultProj = userProjects.find((proj: Project) => proj.is_default);
      if (defaultProj) {
        setDefaultProjectName(defaultProj.name);
        setDefaultProjectId(defaultProj.id);
        setSelectedProjectId(defaultProj.id);
      }
      
      // Only navigate to calendar/auth if that's specifically what the user was trying to access
      if (isCalendarAuthAttempt) {
        navigate(isInnoscaleApp ? '/innoscale/calendar/auth' : '/calendar/auth');
      } else if (isInnoscaleApp) {
        // Navigate to InnoScale dashboard
        navigate('/innoscale/dashboard');
      }
      // Portal sign-ins (outpost/employee) handle their own navigation in PortalSignIn.tsx
    } else {
      // If no projects, go to new project unless trying to access calendar/auth
      if (isCalendarAuthAttempt) {
        navigate(isInnoscaleApp ? '/innoscale/calendar/auth' : '/calendar/auth');
      } else if (isInnoscaleApp) {
        navigate('/innoscale/new-project');
      }
      // Portal sign-ins (outpost/employee) handle their own navigation in PortalSignIn.tsx
    }
  };

  const handleSignOut = async () => {
    cleanupPusher();
    
    // Determine which app the user is currently in
    const isInnoscaleApp = location.pathname.startsWith('/innoscale');
    
    try {
      await axiosRequest(`${authURL}signout/`, 'post');
    } catch (error) {
      console.error('Error during sign out:', error);
    }
    setIsAuthenticated(false);
    
    // Navigate to the appropriate sign-in page
    if (isInnoscaleApp) {
      navigate('/innoscale/signin');
    } else {
      navigate('/outpost/signin');
    }
    
    // Reset TechScouting context
    setTechResponse(null);
    setSelectedCompany(null);
    setSelectedCompanyOrigin(null);
    setSkipFirstPage(undefined);
    
    // Reset StakeholderIdentification context
    setStakeholderResponse([]);
    setSelectedStakeholder(null);
    setSelectedStakeholderOrigin(null);
    
    // Reset MarketResearch context
    setMarketResearchCompanies([]);
    setMarketResearchPrompt('');
    setQuadrantCuts({x_cut: 0.5, y_cut: 0.5});
    setAxisRanges({x_min: 0, x_max: 1, y_min: 0, y_max: 1});
    
    // Reset user and project information
    setUserEmail('');
    setUserId('');
    setIsInProposalsGroup(false);
    setDefaultProjectName('');
    setDefaultProjectId('');
    setSelectedProjectId(null);
    setProjects([]);
  };

  // Define the removeProjectFromList callback.
  const removeProjectFromList = (projectId: string) => {
    setProjects((prevProjects) => prevProjects.filter((proj) => proj.id !== projectId));
    if (selectedProjectId === projectId) {
      setSelectedProjectId(null);
      navigate('/techscouting');
      toast.info('Project has been deleted and you have been redirected.', {
        position: 'top-right',
        autoClose: 5000,
        theme: 'dark',
      });
    }
  };

  return (
    <div className="App">
      <ProjectContext.Provider
        value={{
          defaultProjectId,
          setDefaultProjectId,
          defaultProjectName,
          setDefaultProjectName,
          selectedProjectId,
          setSelectedProjectId,
        }}
      >
        <TechScoutingContext.Provider
          value={{
            techResponse,
            setTechResponse,
            selectedCompany,
            setSelectedCompany,
            selectedCompanyOrigin,
            setSelectedCompanyOrigin,
            skipFirstPage,
            setSkipFirstPage,
          }}
        >
          <StakeholderIdentificationContext.Provider
            value={{
              stakeholderResponse,
              setStakeholderResponse,
              selectedStakeholder,
              setSelectedStakeholder,
              selectedStakeholderOrigin,
              setSelectedStakeholderOrigin
            }}
          >
            <MarketResearchContext.Provider
              value={{
                marketResearchCompanies,
                setMarketResearchCompanies,
                marketResearchPrompt,
                setMarketResearchPrompt,
                quadrantCuts,
                setQuadrantCuts,
                axisRanges,
                setAxisRanges
              }}
            >
              {/* Conditionally render the Navbar */}
              {showUI && (
                <Navbar
                  userEmail={userEmail}
                  isDarkMode={isDarkMode}
                  projects={projects}
                  setCurrentProjectId={setSelectedProjectId}
                  selectedProjectId={selectedProjectId}
                />
              )}
              <div className={`content ${!showUI ? 'full-screen' : ''}`} id="main-content" tabIndex={-1}>
                <Suspense fallback={<div className="loading-screen">Loading...</div>}>
                  <Routes>
                    {/* Public route for calendar one-pager */}
                    <Route path="/calendar/onePager/:ID" element={<LoadingOnePager />} />

                    {/* InnoScale AI Routes (Legacy) */}
                    <Route
                      path="/innoscale/*"
                      element={
                        <InnoscaleApp
                          isDarkMode={isDarkMode}
                          isAuthenticated={isAuthenticated}
                          isInProposalsGroup={isInProposalsGroup}
                          userEmail={userEmail}
                          selectedProjectId={selectedProjectId}
                          selectedLibraryProjectId={selectedLibraryProjectId}
                          projects={projects}
                          setSelectedProjectId={setSelectedProjectId}
                          setSelectedLibraryProjectId={setSelectedLibraryProjectId}
                          updateProjectInList={(updatedProject: ProjectData) => {
                            setProjects((prevProjects) =>
                              prevProjects.map((proj) =>
                                proj.id === updatedProject.id ? { ...proj, name: updatedProject.name } : proj
                              )
                            );
                          }}
                          removeProjectFromList={removeProjectFromList}
                          handleSignOut={handleSignOut}
                          toggleDarkMode={toggleDarkMode}
                          onSignIn={handleSignIn}
                          fetchProjects={async () => {
                            try {
                              const response = await axiosRequest(`${projectsURL}`, 'get');
                              const sortedProjects: Project[] = response.data.sort(
                                (a: Project, b: Project) =>
                                  new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                              );
                              setProjects(sortedProjects);
                              const defaultProj = sortedProjects.find((proj: Project) => proj.is_default);
                              if (defaultProj) {
                                setDefaultProjectName(defaultProj.name);
                                setDefaultProjectId(defaultProj.id);
                                setSelectedProjectId(defaultProj.id);
                              }
                            } catch (error) {
                              console.error('Error fetching projects after new project created:', error);
                            }
                          }}
                        />
                      }
                    />

                    {/* Outpost Portal Routes (Client Portal) */}
                    <Route
                      path="/outpost/*"
                      element={
                        <OutpostPortal
                          isDarkMode={isDarkMode}
                          isAuthenticated={isAuthenticated}
                          userEmail={userEmail}
                          handleSignOut={handleSignOut}
                          toggleDarkMode={toggleDarkMode}
                          onSignIn={handleSignIn}
                        />
                      }
                    />

                    {/* Employee Portal Routes */}
                    <Route
                      path="/employee/*"
                      element={
                        <EmployeePortal
                          isDarkMode={isDarkMode}
                          isAuthenticated={isAuthenticated}
                          userEmail={userEmail}
                          handleSignOut={handleSignOut}
                          toggleDarkMode={toggleDarkMode}
                          onSignIn={handleSignIn}
                        />
                      }
                    />

                    {/* Root Route - Default to Outpost Portal */}
                    <Route path="/" element={<Navigate to="/outpost/signin" replace />} />
                    
                    {/* Fallback Route */}
                    <Route path="*" element={<Navigate to="/outpost/signin" replace />} />
                  </Routes>
                </Suspense>
              </div>
              {/* Conditionally render the SiteMap */}
              {showUI && (
                <SiteMap
                  isDarkMode={isDarkMode}
                  isInProposalsGroup={isInProposalsGroup}
                  setSelectedLibraryProjectId={setSelectedLibraryProjectId}
                />
              )}
              <ToastContainer />
            </MarketResearchContext.Provider>
          </StakeholderIdentificationContext.Provider>
        </TechScoutingContext.Provider>
      </ProjectContext.Provider>
    </div>
  );
};

export default App;
